import type {
  ConversaResumo,
  DesempenhoDoDia,
  ResumoCaixaEntrada,
  StatusPresenca,
} from '@mymaxx/shared';
import { MOTIVOS_PAUSA } from '@mymaxx/shared';
import {
  ChevronDown,
  ChevronRight,
  EyeOff,
  MessagesSquare,
  PhoneOff,
  Star,
  UserPlus,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useNaoLidasInternas } from '../lib/interno-tempo-real';
import { desde } from './ListaConversas';
import { MenuPerfil } from './MenuPerfil';
import { MeuDia } from './MeuDia';

/**
 * Coluna da esquerda da estação: quem eu sou, como estou, como estou indo hoje, quantos
 * esperam e a lista do que está comigo. A fila em si não aparece — o atendente não
 * escolhe cliente, ele pede o próximo (decisão de 24/09, no modelo do Blip Desk).
 */
export function BarraAtendente({
  status,
  motivoPausa,
  invisivel,
  resumo,
  desempenho,
  minhas,
  selecionada,
  puxando,
  onStatus,
  onInvisivel,
  onPuxar,
  onSelecionar,
  onSair,
}: {
  status: StatusPresenca;
  motivoPausa: string | null;
  invisivel: boolean;
  resumo: ResumoCaixaEntrada | undefined;
  desempenho: DesempenhoDoDia | undefined;
  minhas: ConversaResumo[];
  selecionada: string | null;
  puxando: boolean;
  onStatus: (status: StatusPresenca, motivo?: string) => void;
  onInvisivel: (invisivel: boolean) => void;
  onPuxar: () => void;
  onSelecionar: (id: string) => void;
  onSair: () => void;
}) {
  // A conferência do dia era só um item no menu atrás da foto, e ninguém achava (29/09).
  // Agora o resumo do dia, que já está à vista, é o próprio botão.
  const [verMeuDia, setVerMeuDia] = useState(false);
  const naFila = resumo?.filaPorSetor.reduce((total, s) => total + s.naFila, 0) ?? 0;
  const esperaMaior = Math.max(0, ...(resumo?.filaPorSetor ?? []).map((s) => s.esperaMaiorSeg));
  const ativas = resumo?.minhasAtivas ?? minhas.length;
  const limite = resumo?.limiteSimultaneo ?? 0;
  const noLimite = limite > 0 && ativas >= limite;

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950">
      <div className="space-y-2 border-b border-neutral-200 p-3 dark:border-neutral-800">
        <MenuPerfil aoSair={onSair} aoVerMeuDia={() => setVerMeuDia(true)} />
        <NotaDoDia desempenho={desempenho} aoAbrir={() => setVerMeuDia(true)} />
        <SeletorStatus
          status={status}
          motivoPausa={motivoPausa}
          invisivel={invisivel}
          onEscolher={onStatus}
          onInvisivel={onInvisivel}
        />
      </div>

      {verMeuDia && <MeuDia onFechar={() => setVerMeuDia(false)} />}

      {/* Quem está esperando + puxar */}
      <div className="border-b border-neutral-200 p-3 text-center dark:border-neutral-800">
        <p className="text-3xl leading-none font-semibold tabular-nums">{naFila}</p>
        <p className="mt-1 text-xs leading-tight text-neutral-500">
          {naFila === 1 ? 'cliente esperando' : 'clientes esperando'}
          {naFila > 0 && esperaMaior > 0 && (
            <span className="block">
              mais antigo há {desde(new Date(Date.now() - esperaMaior * 1000).toISOString())}
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={onPuxar}
          disabled={puxando}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-marca-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-marca-700 disabled:opacity-60"
        >
          <UserPlus size={15} /> {puxando ? 'Puxando…' : 'Atender novo cliente'}
        </button>
        {noLimite && (
          // O limite governa a distribuição automática; puxar na mão continua liberado.
          <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
            Você está com {ativas} de {limite}. Puxar mais é decisão sua.
          </p>
        )}
      </div>

      {/* O que está comigo */}
      <p className="px-3 py-2 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
        {ativas === 1 ? '1 atendimento em andamento' : `${ativas} atendimentos em andamento`}
      </p>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {minhas.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-neutral-500">
            Nenhum atendimento com você. Puxe o próximo quando estiver pronto.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {minhas.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onSelecionar(c.id)}
                  className={`w-full px-3 py-2.5 text-left transition ${
                    selecionada === c.id
                      ? 'bg-white shadow-sm dark:bg-neutral-900'
                      : 'hover:bg-neutral-100 dark:hover:bg-neutral-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {c.cliente ?? 'Ainda sem identificação'}
                    </span>
                    {c.naoLidas > 0 && (
                      <span className="shrink-0 rounded-full bg-marca-600 px-1.5 text-[11px] font-medium text-white">
                        {c.naoLidas}
                      </span>
                    )}
                  </div>
                  <p className="truncate font-mono text-[10px] text-neutral-400">{c.protocolo}</p>
                  <p className="mt-0.5 truncate text-xs text-neutral-500">
                    {c.ultimaMensagem ?? 'Sem mensagens'}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

/**
 * Como o atendente está indo hoje — a régua dele, não a da supervisão. Clicar abre a
 * lista do dia: os números levantam a pergunta "quais foram?", e a resposta tem de estar
 * a um clique, não escondida num menu.
 */
function NotaDoDia({
  desempenho,
  aoAbrir,
}: {
  desempenho: DesempenhoDoDia | undefined;
  aoAbrir: () => void;
}) {
  if (!desempenho) return null;

  const { notaMedia, avaliados, encerrados } = desempenho;
  return (
    <button
      type="button"
      onClick={aoAbrir}
      title="Ver os atendimentos que passaram por você hoje"
      className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-2 py-1.5 text-xs transition hover:bg-neutral-100 dark:bg-neutral-900 dark:hover:bg-neutral-800"
    >
      <span className="flex items-center gap-1">
        <Star
          size={13}
          className={notaMedia === null ? 'text-neutral-400' : corDaNota(notaMedia)}
          fill={notaMedia === null ? 'none' : 'currentColor'}
        />
        {notaMedia === null ? (
          <span className="text-neutral-500">Sem avaliações hoje</span>
        ) : (
          <>
            <strong className={corDaNota(notaMedia)}>{notaMedia.toFixed(1)}</strong>
            <span className="text-neutral-500">
              de 5 · {avaliados} {avaliados === 1 ? 'avaliação' : 'avaliações'}
            </span>
          </>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-1 text-neutral-400">
        {encerrados} hoje <ChevronRight size={13} aria-hidden="true" />
      </span>
    </button>
  );
}

function corDaNota(nota: number): string {
  if (nota >= 4) return 'text-green-600 dark:text-green-400';
  if (nota >= 3) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/**
 * Um campo só: disponível, ou a pausa com o motivo. "Ausente" saiu da lista porque não
 * é uma escolha — é o que o sistema marca quando a pessoa fecha a aba ou some
 * (decisão de 24/09).
 */
/**
 * O status de quem atende — que é também o status dele na conversa interna.
 *
 * Um controle só, e não dois (08/10): "Reunião" já existia como motivo de pausa, e um
 * segundo seletor com a mesma palavra querendo dizer outra coisa seria duas listas para a
 * pessoa manter em dia e esquecer de voltar.
 */
function SeletorStatus({
  status,
  motivoPausa,
  invisivel,
  onEscolher,
  onInvisivel,
}: {
  status: StatusPresenca;
  motivoPausa: string | null;
  invisivel: boolean;
  onEscolher: (status: StatusPresenca, motivo?: string) => void;
  onInvisivel: (invisivel: boolean) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const rotulo =
    (status === 'online'
      ? 'Disponível'
      : status === 'pausa'
        ? `Pausa · ${motivoPausa ?? 'sem motivo'}`
        : 'Ausente') + (invisivel ? ' · invisível' : '');

  function escolher(novo: StatusPresenca, motivo?: string) {
    setAberto(false);
    onEscolher(novo, motivo);
  }

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        className="flex w-full items-center gap-2 rounded-lg border border-neutral-300 px-2 py-1.5 text-xs transition hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
      >
        <span className={`size-2 shrink-0 rounded-full ${bolinha(status)}`} aria-hidden />
        <span className="min-w-0 flex-1 truncate text-left">{rotulo}</span>
        <ChevronDown size={14} className="shrink-0 text-neutral-400" />
      </button>

      {aberto && (
        <ul
          role="listbox"
          className="absolute top-full right-0 left-0 z-30 mt-1 overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={status === 'online'}
              onClick={() => escolher('online')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <span className="size-2 rounded-full bg-green-500" aria-hidden /> Disponível
            </button>
          </li>
          <li className="px-3 pt-2 pb-1 text-[10px] font-medium tracking-wide text-neutral-400 uppercase">
            Entrar em pausa
          </li>
          {MOTIVOS_PAUSA.map((m) => (
            <li key={m.rotulo}>
              <button
                type="button"
                role="option"
                aria-selected={status === 'pausa' && motivoPausa === m.rotulo}
                onClick={() => escolher('pausa', m.rotulo)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition hover:bg-neutral-100 dark:hover:bg-neutral-800 ${
                  status === 'pausa' && motivoPausa === m.rotulo ? 'font-medium' : ''
                }`}
              >
                <span className="size-2 rounded-full bg-amber-500" aria-hidden />
                <span className="flex-1">{m.rotulo}</span>
                {/* Dito na própria lista, e não escondido num aviso: a diferença entre
                  "saí cinco minutos" e "não me liguem" é a razão de haver dois tipos de
                  pausa, e quem escolhe precisa ver isso na hora de escolher. */}
                {m.barraLigacao && (
                  <span
                    title="Não recebe ligações"
                    className="flex items-center gap-1 text-[10px] text-neutral-400"
                  >
                    <PhoneOff size={11} /> sem ligação
                  </span>
                )}
              </button>
            </li>
          ))}

          {/* Chave à parte, e não um item da lista: invisível convive com qualquer estado.
            Dá para estar em reunião E invisível, ou disponível e invisível. */}
          <li className="mt-1 border-t border-neutral-200 pt-1 dark:border-neutral-700">
            <button
              type="button"
              onClick={() => {
                setAberto(false);
                onInvisivel(!invisivel);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <EyeOff size={12} className={invisivel ? 'text-marca-600' : 'text-neutral-400'} />
              <span className="flex-1">{invisivel ? 'Voltar a aparecer' : 'Ficar invisível'}</span>
              <span className="text-[10px] text-neutral-400">ligação continua</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

function bolinha(status: StatusPresenca): string {
  if (status === 'online') return 'bg-green-500';
  if (status === 'pausa') return 'bg-amber-500';
  return 'bg-neutral-400';
}

/**
 * A porta para a conversa da equipe, agora na faixa do topo.
 *
 * Morava aqui na coluna da esquerda, encostada no seletor de status — e o pessoal clicava
 * numa querendo a outra o dia inteiro (relato da operação, 08/10). Trocar de canto é o que
 * resolve: mover uns pixels dentro da mesma coluna não resolveria.
 */
export function AtalhoConversas() {
  const naoLidas = useNaoLidasInternas();
  return (
    <Link
      to="/interno"
      className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-neutral-600 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
    >
      <MessagesSquare size={15} /> Conversas
      {naoLidas > 0 && (
        <span className="rounded-full bg-marca-600 px-1.5 text-[11px] font-medium text-white">
          {naoLidas > 99 ? '99+' : naoLidas}
        </span>
      )}
    </Link>
  );
}
